import React, { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, Text, TextInput, View } from 'react-native';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ModalShell } from '../../shared/modals/ModalShell';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { showError, showSuccess } from '../../shared/ui/appToast';
import type { OperationalConfirmOptions } from '../../shared/ui/useOperationalConfirm';
import {
  deleteStation,
  listProductNamesForBranch,
  setKitchenStationPrinter,
  setProductStations,
  upsertStation,
} from '../infra/hospitalityRepo';
import type { ProductStation, Station } from '../domain/hospitalityTypes';
import { kitchenQueueStyles as styles } from './KitchenQueueScreen.styles';

type ConfirmFn = (request: OperationalConfirmOptions) => Promise<boolean>;

/**
 * Estado del panel elevado a KitchenQueueScreen: el header vive dentro de ramas
 * (loading / vacío / lista / tablero) que se remontan al llegar el snapshot, y un
 * estado local se pierde a mitad de edición. El padre lo conserva entre remounts.
 */
export type StationsAdminPanelState = {
  nameForm: Station | 'create' | null;
  nameDraft: string;
  assigningStation: Station | null;
  printerEditingStationId: string | null;
  printerDraft: string;
  productList: Array<{ id: string; name: string }>;
};

export const initialStationsAdminPanelState: StationsAdminPanelState = {
  nameForm: null,
  nameDraft: '',
  assigningStation: null,
  printerEditingStationId: null,
  printerDraft: '',
  productList: [],
};

export type KitchenStationsAdminPanelProps = {
  visible: boolean;
  onClose: () => void;
  branchId: string | null;
  stations: Station[];
  productStations: ProductStation[];
  stationPrintersMap: Record<string, string>;
  onStationPrintersMapChange: (map: Record<string, string>) => void;
  onAfterChange: () => Promise<void>;
  onStationDeleted: (stationId: string) => void;
  confirm: ConfirmFn;
  panelState: StationsAdminPanelState;
  onPanelStateChange: (patch: Partial<StationsAdminPanelState>) => void;
};

function stationProductCount(productStations: ProductStation[], stationId: string): number {
  return productStations.filter((ps) => ps.stationId === stationId).length;
}

function stationMetaLine(
  productCount: number,
  printerName: string | undefined,
): string {
  const products =
    productCount === 0
      ? 'Sin productos asignados'
      : productCount === 1
        ? '1 producto'
        : `${productCount} productos`;
  const printer = printerName ? `Impresora: ${printerName}` : 'Sin impresora';
  return `${products} · ${printer}`;
}

export function KitchenStationsAdminPanel({
  visible,
  onClose,
  branchId,
  stations,
  productStations,
  stationPrintersMap,
  onStationPrintersMapChange,
  onAfterChange,
  onStationDeleted,
  confirm,
  panelState,
  onPanelStateChange,
}: KitchenStationsAdminPanelProps) {
  const { nameDraft, assigningStation, printerEditingStationId, printerDraft, productList } =
    panelState;
  const editingStation = panelState.nameForm === 'create' ? null : panelState.nameForm;

  const [stationSaving, setStationSaving] = useState(false);
  const [savingPrinter, setSavingPrinter] = useState(false);
  const [productListLoading, setProductListLoading] = useState(false);
  const [productToggleSaving, setProductToggleSaving] = useState(false);

  const showNameForm = panelState.nameForm != null;

  const productCountByStation = useMemo(() => {
    const map = new Map<string, number>();
    for (const station of stations) {
      map.set(station.id, stationProductCount(productStations, station.id));
    }
    return map;
  }, [stations, productStations]);

  const resetNameForm = () => onPanelStateChange({ nameForm: null, nameDraft: '' });

  const handleSaveStation = async () => {
    if (!branchId) return;
    const name = nameDraft.trim();
    if (!name) {
      showError('Estaciones', 'El nombre es obligatorio');
      return;
    }
    setStationSaving(true);
    try {
      await upsertStation({
        branchId,
        name,
        stationId: editingStation?.id ?? null,
      });
      resetNameForm();
      await onAfterChange();
      showSuccess('Estaciones', editingStation ? 'Estación actualizada' : 'Estación creada');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'estaciones',
        error: e,
        title: 'Estaciones',
        userMessage: 'No se pudo guardar',
      });
    } finally {
      setStationSaving(false);
    }
  };

  const handleDeleteStation = async (station: Station) => {
    const ok = await confirm({
      title: 'Eliminar estación',
      message: `¿Seguro que querés eliminar "${station.name}"? Los productos asignados dejarán de filtrarse por esta estación.`,
      confirmLabel: 'Eliminar',
      destructive: true,
      iconName: 'trash-outline',
    });
    if (!ok) return;
    setStationSaving(true);
    try {
      await deleteStation(station.id);
      onPanelStateChange({
        ...(assigningStation?.id === station.id ? { assigningStation: null } : {}),
        ...(printerEditingStationId === station.id
          ? { printerEditingStationId: null, printerDraft: '' }
          : {}),
      });
      onStationDeleted(station.id);
      await onAfterChange();
      showSuccess('Estaciones', 'Estación eliminada');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'estaciones',
        error: e,
        title: 'Estaciones',
        userMessage: 'No se pudo eliminar',
      });
    } finally {
      setStationSaving(false);
    }
  };

  const handleOpenProductAssign = async (station: Station) => {
    if (!branchId) return;
    if (assigningStation?.id === station.id) {
      onPanelStateChange({ assigningStation: null });
      return;
    }
    onPanelStateChange({
      assigningStation: station,
      printerEditingStationId: null,
      printerDraft: '',
    });
    setProductListLoading(true);
    try {
      const products = await listProductNamesForBranch(branchId);
      onPanelStateChange({ productList: products });
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'estaciones',
        error: e,
        title: 'Estaciones',
        userMessage: 'No se pudieron cargar productos',
      });
    } finally {
      setProductListLoading(false);
    }
  };

  const handleToggleProductStation = async (productId: string) => {
    if (!assigningStation || !branchId) return;
    setProductToggleSaving(true);
    try {
      const currentStationIds = productStations
        .filter((ps) => ps.productId === productId)
        .map((ps) => ps.stationId);
      const isAssigned = currentStationIds.includes(assigningStation.id);
      const newStationIds = isAssigned
        ? currentStationIds.filter((id) => id !== assigningStation.id)
        : [...currentStationIds, assigningStation.id];
      await setProductStations(productId, newStationIds, branchId);
      await onAfterChange();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'estaciones',
        error: e,
        title: 'Estaciones',
        userMessage: 'No se pudo asignar',
      });
    } finally {
      setProductToggleSaving(false);
    }
  };

  const handleSaveStationPrinter = async (stationId: string) => {
    if (savingPrinter) return;
    const name = printerDraft.trim();
    const newMap = { ...stationPrintersMap };
    if (name) {
      newMap[stationId] = name;
    } else {
      delete newMap[stationId];
    }
    setSavingPrinter(true);
    try {
      if (!branchId) return;
      await setKitchenStationPrinter(branchId, stationId, name);
      onStationPrintersMapChange(newMap);
      onPanelStateChange({ printerEditingStationId: null, printerDraft: '' });
      showSuccess('Impresoras', name ? 'Impresora asignada' : 'Impresora removida');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'impresoras',
        error: e,
        title: 'Impresoras',
        userMessage: 'No se pudo guardar',
      });
    } finally {
      setSavingPrinter(false);
    }
  };

  const openPrinterEditor = (station: Station) => {
    if (printerEditingStationId === station.id) {
      onPanelStateChange({ printerEditingStationId: null, printerDraft: '' });
      return;
    }
    onPanelStateChange({
      assigningStation: null,
      printerEditingStationId: station.id,
      printerDraft: stationPrintersMap[station.id] ?? '',
    });
  };

  const openRename = (station: Station) => {
    onPanelStateChange({
      nameForm: station,
      nameDraft: station.name,
      assigningStation: null,
      printerEditingStationId: null,
      printerDraft: '',
    });
  };

  const renderProductPicker = (station: Station) => {
    if (assigningStation?.id !== station.id) return null;
    return (
      <View style={styles.stationInlinePanel} testID="hospitality.cocina.stations-products">
        <Text style={styles.productAssignTitle}>Marcá qué productos salen en {station.name}</Text>
        {productListLoading ? (
          <ActivityIndicator color={ticketColors.gold} style={{ marginVertical: spacing.sm }} />
        ) : productList.length === 0 ? (
          <Text style={styles.noProductsText}>No hay productos en esta sucursal.</Text>
        ) : (
          <ScrollView style={styles.productListScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {productList.map((p) => {
              const isAssigned = productStations.some(
                (ps) => ps.productId === p.id && ps.stationId === station.id,
              );
              return (
                <SoftPressable
                  key={p.id}
                  onPress={() => void handleToggleProductStation(p.id)}
                  disabled={productToggleSaving}
                  style={[styles.productRow, isAssigned && styles.productRowActive]}
                  accessibilityRole="button"
                  accessibilityLabel={`${isAssigned ? 'Quitar' : 'Agregar'} ${p.name} en ${station.name}`}
                >
                  <Text style={[styles.productRowLabel, isAssigned && styles.productRowLabelActive]}>
                    {isAssigned ? '✓ ' : ''}
                    {p.name}
                  </Text>
                </SoftPressable>
              );
            })}
          </ScrollView>
        )}
      </View>
    );
  };

  const renderPrinterEditor = (station: Station) => {
    if (printerEditingStationId !== station.id) return null;
    return (
      <View style={styles.stationInlinePanel} testID="hospitality.cocina.stations-printer">
        <Text style={styles.productAssignTitle}>Impresora térmica para {station.name}</Text>
        <Text style={styles.stationMeta}>
          Poné el nombre de la impresora en la red. Cuando imprimís una comanda de esta estación, sale ahí.
        </Text>
        <View style={styles.stationEditRow}>
          <TextInput
            style={styles.stationInput}
            value={printerDraft}
            onChangeText={(v) => onPanelStateChange({ printerDraft: v })}
            placeholder="Ej. Cocina, Barra"
            placeholderTextColor={ticketColors.ink3}
            accessibilityLabel="Nombre de la impresora"
            autoFocus
          />
          <SoftPressable
            onPress={() => void handleSaveStationPrinter(station.id)}
            disabled={savingPrinter}
            hoverLift={false}
            style={[styles.stationActionBtn, styles.stationSaveBtn, savingPrinter && { opacity: 0.45 }]}
            accessibilityRole="button"
            accessibilityLabel="Guardar impresora"
          >
            <Text style={styles.stationActionLabelPrimary}>Guardar</Text>
          </SoftPressable>
          <SoftPressable
            onPress={() => {
              if (savingPrinter) return;
              onPanelStateChange({ printerEditingStationId: null, printerDraft: '' });
            }}
            disabled={savingPrinter}
            style={[styles.stationActionBtn, savingPrinter && { opacity: 0.45 }]}
            accessibilityRole="button"
            accessibilityLabel="Cancelar impresora"
          >
            <Text style={styles.stationActionLabel}>Cancelar</Text>
          </SoftPressable>
        </View>
      </View>
    );
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Estaciones de cocina"
      kicker="Cocina"
      widthVariant="comfortable"
      closeAccessibilityLabel="Cerrar estaciones"
    >
      <View style={styles.adminPanel} testID="hospitality.cocina.stations-admin-panel">
      <Text style={styles.adminPanelIntro}>
        Separá la pantalla por área (Cocina, Barra, Postre). Elegí qué productos van a cada una y, si tenés,
        a qué impresora sale la comanda.
      </Text>

      {showNameForm ? (
        <View style={styles.stationEditRow}>
          <TextInput
            style={styles.stationInput}
            value={nameDraft}
            onChangeText={(v) => onPanelStateChange({ nameDraft: v })}
            placeholder="Nombre de la estación"
            placeholderTextColor={ticketColors.ink3}
            accessibilityLabel="Nombre de la estación"
            autoFocus
          />
          <SoftPressable
            onPress={() => void handleSaveStation()}
            disabled={stationSaving}
            hoverLift={false}
            style={[styles.stationActionBtn, styles.stationSaveBtn]}
            accessibilityRole="button"
            accessibilityLabel="Guardar estación"
          >
            <Text style={styles.stationActionLabelPrimary}>Guardar</Text>
          </SoftPressable>
          <SoftPressable
            onPress={resetNameForm}
            style={styles.stationActionBtn}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={styles.stationActionLabel}>Cancelar</Text>
          </SoftPressable>
        </View>
      ) : null}

      {stations.map((s) => {
        const productCount = productCountByStation.get(s.id) ?? 0;
        const printerName = stationPrintersMap[s.id];
        const productsOpen = assigningStation?.id === s.id;
        const printerOpen = printerEditingStationId === s.id;

        return (
          <View key={s.id} style={styles.stationCard} testID={`hospitality.cocina.station.${s.id}`}>
            <Text style={styles.stationNameText}>{s.name}</Text>
            <Text style={styles.stationMeta}>{stationMetaLine(productCount, printerName)}</Text>
            <View style={styles.stationActionsRow}>
              <SoftPressable
                onPress={() => void handleOpenProductAssign(s)}
                style={[styles.stationActionBtn, productsOpen && styles.productRowActive]}
                accessibilityRole="button"
                accessibilityLabel={`Elegir productos de ${s.name}`}
                accessibilityHint="Filtrá qué comandas aparecen cuando elegís esta estación arriba"
              >
                <Text style={[styles.stationActionLabel, productsOpen && styles.productRowLabelActive]}>
                  {productsOpen ? 'Cerrar lista' : 'Elegir productos'}
                </Text>
              </SoftPressable>
              <SoftPressable
                onPress={() => openPrinterEditor(s)}
                style={[styles.stationActionBtn, printerOpen && styles.productRowActive]}
                accessibilityRole="button"
                accessibilityLabel={`Impresora de ${s.name}`}
                accessibilityHint="Nombre de la impresora térmica en la red"
              >
                <Text style={[styles.stationActionLabel, printerOpen && styles.productRowLabelActive]}>
                  {printerOpen ? 'Cerrar impresora' : 'Impresora'}
                </Text>
              </SoftPressable>
              <SoftPressable
                onPress={() => openRename(s)}
                style={styles.stationActionBtn}
                accessibilityRole="button"
                accessibilityLabel={`Renombrar ${s.name}`}
              >
                <Text style={styles.stationActionLabel}>Renombrar</Text>
              </SoftPressable>
              <SoftPressable
                onPress={() => void handleDeleteStation(s)}
                disabled={stationSaving}
                style={[styles.stationActionBtn, styles.stationDeleteBtn]}
                accessibilityRole="button"
                accessibilityLabel={`Eliminar ${s.name}`}
              >
                <Text style={styles.stationActionLabelDanger}>Eliminar</Text>
              </SoftPressable>
            </View>
            {renderProductPicker(s)}
            {renderPrinterEditor(s)}
          </View>
        );
      })}

      {!showNameForm ? (
        <SoftPressable
          onPress={() =>
            onPanelStateChange({
              nameForm: 'create',
              nameDraft: '',
              assigningStation: null,
              printerEditingStationId: null,
              printerDraft: '',
            })
          }
          style={[styles.stationActionBtn, styles.stationAddBtn]}
          accessibilityRole="button"
          accessibilityLabel="Nueva estación"
        >
          <Text style={styles.stationActionLabelAccent}>+ Nueva estación</Text>
        </SoftPressable>
      ) : null}
      </View>
    </ModalShell>
  );
}
